/**
 * Patient Diagnosis API Service
 * Handles all API calls for Patient Diagnosis module
 *
 * @author CareX Frontend Team
 */

import apiClient from './apiClient';
import { formatToBackendDate } from '../utils/dateUtils';
import {
  PatientDiagnosisDTO,
  CreatePatientDiagnosisRequest,
  ExamVitalsDTO,
  AmbulatoryStatusDTO,
  DiagnosesTreatmentsDTO,
  PhysicalHealthAssessmentDTO,
  MentalConditionAssessmentDTO,
  CapacityMedicationAssessmentDTO,
  HospicePhysicianInfoDTO,
  DiagnosisStatus,
  PageResponse,
} from '../types/diagnosis.types';

const BASE_PATH = '/v1/patient-diagnosis';

/**
 * Patient Diagnosis Service
 * Provides type-safe API calls with proper error handling
 */
class PatientDiagnosisService {
  private normalizeStatus(status: any): string {
    // Backend may return statuses like "InProgress" instead of "IN_PROGRESS".
    // Normalize by keeping only letters and uppercasing.
    return String(status ?? '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '');
  }

  // ========== Create Operations ==========

  /**
   * Create a new patient diagnosis (DRAFT status)
   * @param request - Create request with resident ID and diagnosis date
   * @returns Created diagnosis DTO
   */
  async createDiagnosis(
    request: CreatePatientDiagnosisRequest
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.post<PatientDiagnosisDTO>(
      BASE_PATH,
      request
    );
    return response.data;
  }

  /**
   * Update an existing patient diagnosis (PUT)
   * @param diagnosisId - The ID of the diagnosis to update
   * @param request - Full update request
   * @returns Updated diagnosis DTO
   */
  async updateDiagnosis(
    diagnosisId: string,
    request: CreatePatientDiagnosisRequest
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.put<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}`,
      request
    );
    return response.data;
  }

  // ========== Tab Update Operations (7 methods) ==========

  /**
   * Update Exam & Vitals (Tab 1)
   */
  async updateExamVitals(
    diagnosisId: string,
    data: ExamVitalsDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/exam-vitals`,
      data
    );
    return response.data;
  }

  /**
   * Update Ambulatory Status (Tab 2)
   */
  async updateAmbulatoryStatus(
    diagnosisId: string,
    data: AmbulatoryStatusDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/ambulatory-status`,
      data
    );
    return response.data;
  }

  /**
   * Update Diagnosis & Treatments (Tab 3)
   */
  async updateDiagnosesTreatments(
    diagnosisId: string,
    data: DiagnosesTreatmentsDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/diagnoses-treatments`,
      data
    );
    return response.data;
  }

  /**
   * Update Physical Health Assessment (Tab 4)
   */
  async updatePhysicalHealthAssessment(
    diagnosisId: string,
    data: PhysicalHealthAssessmentDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/physical-health`,
      data
    );
    return response.data;
  }

  /**
   * Update Mental Condition Assessment (Tab 5)
   */
  async updateMentalConditionAssessment(
    diagnosisId: string,
    data: MentalConditionAssessmentDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/mental-condition`,
      data
    );
    return response.data;
  }

  /**
   * Update Capacity & Medication Assessment (Tab 6)
   */
  async updateCapacityMedicationAssessment(
    diagnosisId: string,
    data: CapacityMedicationAssessmentDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/capacity-medication`,
      data
    );
    return response.data;
  }

  /**
   * Update Hospice & Physician Info (Tab 7)
   */
  async updateHospicePhysicianInfo(
    diagnosisId: string,
    data: HospicePhysicianInfoDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/hospice-physician`,
      data
    );
    return response.data;
  }

  // ========== Read Operations ==========

  /**
   * Get diagnosis by ID
   */
  async getDiagnosisById(id: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.get<PatientDiagnosisDTO>(
      `${BASE_PATH}/${id}`
    );
    return response.data;
  }

  /**
   * Get all diagnoses for a resident
   */
  async getDiagnosesByResident(
    residentId: string
  ): Promise<PatientDiagnosisDTO[]> {
    const response = await apiClient.get<PatientDiagnosisDTO[]>(
      `${BASE_PATH}/resident/${residentId}`
    );
    return response.data;
  }

  /**
   * Get latest diagnosis for a resident
   */
  async getLatestDiagnosis(residentId: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.get<PatientDiagnosisDTO>(
      `${BASE_PATH}/resident/${residentId}/latest`
    );
    return response.data;
  }

  /**
   * Get diagnoses by status (paginated)
   */
  async getDiagnosesByStatus(
    status: DiagnosisStatus,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<PatientDiagnosisDTO>> {
    const response = await apiClient.get<PageResponse<PatientDiagnosisDTO>>(
      `${BASE_PATH}/status/${status}`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  /**
   * Get diagnoses by facility (paginated)
   */
  async getDiagnosesByFacility(
    facilityId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<PatientDiagnosisDTO>> {
    const response = await apiClient.get<PageResponse<PatientDiagnosisDTO>>(
      `${BASE_PATH}/facility/${facilityId}`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  /**
   * Get diagnoses pending review
   */
  async getPendingReviewDiagnoses(
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<PatientDiagnosisDTO>> {
    const response = await apiClient.get<PageResponse<PatientDiagnosisDTO>>(
      `${BASE_PATH}/pending-review`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  // ========== Workflow Operations ==========

  /**
   * Submit diagnosis for review
   * Marks diagnosis as COMPLETED and validates required fields
   */
  async submitDiagnosis(diagnosisId: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.put<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/submit`
    );
    return response.data;
  }

  /**
   * Review and approve diagnosis
   * Requires admin role
   */
  async reviewDiagnosis(diagnosisId: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.put<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/review`
    );
    return response.data;
  }

  // ========== Delete Operations ==========

  /**
   * Delete diagnosis (soft delete)
   */
  async deleteDiagnosis(diagnosisId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${diagnosisId}`);
  }

  // ========== Helper Methods ==========

  /**
   * Get or create draft diagnosis for a resident
   * Useful for auto-save functionality
   */
  /**
   * Get or create draft diagnosis for a resident
   * Useful for auto-save functionality
   */
  async getOrCreateDraft(
    residentId: string
  ): Promise<PatientDiagnosisDTO> {
    const active = await this.getLatestActive(residentId);
    if (active) return active;

    return await this.createDiagnosis({
      residentId,
      diagnosisDate: formatToBackendDate(new Date()) || "",
    });
  }

  /**
   * Fetches the latest diagnosis for a resident that is still in an editable state.
   * Returns null if no active diagnosis exists.
   */
  async getLatestActive(residentId: string): Promise<PatientDiagnosisDTO | null> {
    try {
      const diagnoses = await this.getDiagnosesByResident(residentId);
      if (!diagnoses || diagnoses.length === 0) return null;

      // Find the most recent diagnosis that isn't REVIEWED
      return diagnoses.find(
        (d) => {
          const s = this.normalizeStatus(d.status);
          return (
            s === this.normalizeStatus(DiagnosisStatus.DRAFT) ||
            s === this.normalizeStatus(DiagnosisStatus.IN_PROGRESS) ||
            s === this.normalizeStatus(DiagnosisStatus.COMPLETED)
          );
        }
      ) || null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  /**
   * Check if diagnosis is editable
   */
  isEditable(diagnosis: PatientDiagnosisDTO): boolean {
    if (!diagnosis || !diagnosis.status) return true;
    
    // Normalize status to uppercase for robust comparison
    const status = this.normalizeStatus(diagnosis.status);
    const draft = this.normalizeStatus(DiagnosisStatus.DRAFT);
    const inProgress = this.normalizeStatus(DiagnosisStatus.IN_PROGRESS);
    const completed = this.normalizeStatus(DiagnosisStatus.COMPLETED);
    
    return (
      status === draft ||
      status === inProgress ||
      status === completed
    );
  }

  /**
   * Get completion status message
   */
  getCompletionMessage(completionPercentage: number): string {
    if (completionPercentage === 0) return 'Not started';
    if (completionPercentage < 30) return 'Just started';
    if (completionPercentage < 70) return 'In Progress';
    if (completionPercentage < 100) return 'Almost complete';
    return 'Complete';
  }
}

// Export singleton instance
export default new PatientDiagnosisService();